import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  BookOpen,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import { format } from 'date-fns';
import type { POSCustomer } from '@/types';
import { formatMoney } from '@/utils/currency';

type StandingTone = 'owe' | 'advance' | 'settled';

interface CustomerDetailHeroProps {
  customer: POSCustomer & { creation?: string | null };
  standingLabel: { tone: StandingTone; text: string };
  lastPurchaseDate?: string | null;
  canEdit: boolean;
  canDelete: boolean;
  onAddPayment: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onLedger: () => void;
  moreOpen: boolean;
  onToggleMore: () => void;
}

function initials(name?: string) {
  const parts = (name || '?').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function CustomerDetailHero({
  customer,
  standingLabel,
  lastPurchaseDate,
  canEdit,
  canDelete,
  onAddPayment,
  onEdit,
  onDelete,
  onLedger,
  moreOpen,
  onToggleMore,
}: CustomerDetailHeroProps) {
  const { t } = useTranslation();
  const displayName = `${customer.customer_name || ''}${
    customer.is_walk_in ? ` ${t('customers.walkIn')}` : ''
  }`;

  const toneClass =
    standingLabel.tone === 'owe'
      ? 'bg-red-50 text-red-700 border-red-200'
      : standingLabel.tone === 'advance'
        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
        : 'bg-slate-100 text-slate-700 border-slate-200';

  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white shadow-sm p-4 sm:p-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 text-white flex items-center justify-center text-sm sm:text-base font-semibold shrink-0 shadow-inner">
            {initials(customer.customer_name)}
          </div>
          <div className="min-w-0 space-y-1.5">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 truncate">
                {displayName}
              </h1>
              {customer.customer_type && (
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                  {customer.customer_type}
                </span>
              )}
              <span
                className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full border ${toneClass}`}
              >
                {standingLabel.tone === 'settled'
                  ? t('customers.dashboard.settled', 'Settled')
                  : standingLabel.text}
              </span>
              {customer.is_default ? (
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 border border-blue-200">
                  {t('common.default')}
                </span>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
              <span className="font-mono text-slate-400">{customer.name}</span>
              {customer.creation && (
                <span>
                  {t('customers.dashboard.customerSince', 'Customer since')}{' '}
                  {format(new Date(customer.creation), 'dd MMM yyyy')}
                </span>
              )}
              {lastPurchaseDate && (
                <span>
                  {t('customers.dashboard.lastPurchase')}:{' '}
                  {format(new Date(lastPurchaseDate), 'dd MMM yyyy')}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Desktop / tablet primary actions */}
        <div className="hidden sm:flex flex-wrap items-center gap-2 shrink-0 relative">
          <Link
            to="/"
            className="btn-primary inline-flex items-center gap-1.5 text-sm px-3.5 py-2"
          >
            <Plus className="w-4 h-4" />
            {t('customers.dashboard.newSale', 'New Sale')}
          </Link>
          <button
            type="button"
            onClick={onAddPayment}
            className="btn-secondary inline-flex items-center gap-1.5 text-sm px-3.5 py-2"
          >
            <Activity className="w-4 h-4" />
            {t('customers.dashboard.addPayment', 'Add Payment')}
          </button>
          {canEdit && (
            <button
              type="button"
              onClick={onEdit}
              className="btn-secondary inline-flex items-center gap-1.5 text-sm px-3.5 py-2"
            >
              <Pencil className="w-4 h-4" />
              {t('common.edit')}
            </button>
          )}
          <div className="relative">
            <button
              type="button"
              onClick={onToggleMore}
              className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
              aria-label={t('common.more', 'More')}
            >
              <MoreHorizontal className="w-5 h-5" />
            </button>
            {moreOpen && (
              <div className="absolute right-0 mt-1 w-44 rounded-xl border border-slate-200 bg-white shadow-lg py-1 z-20">
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 inline-flex items-center gap-2"
                  onClick={onLedger}
                >
                  <BookOpen className="w-4 h-4" />
                  {t('common.ledger')}
                </button>
                {canDelete && (
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 inline-flex items-center gap-2"
                    onClick={onDelete}
                  >
                    <Trash2 className="w-4 h-4" />
                    {t('common.delete')}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Mobile icon actions */}
        <div className="sm:hidden grid grid-cols-4 gap-2">
          <Link
            to="/"
            className="flex flex-col items-center gap-1 rounded-xl bg-primary-50 text-primary-800 py-2.5 border border-primary-100"
          >
            <Plus className="w-5 h-5" />
            <span className="text-[10px] font-semibold">Sale</span>
          </Link>
          <button
            type="button"
            onClick={onAddPayment}
            className="flex flex-col items-center gap-1 rounded-xl bg-slate-50 text-slate-700 py-2.5 border border-slate-200"
          >
            <Activity className="w-5 h-5" />
            <span className="text-[10px] font-semibold">Payment</span>
          </button>
          {canEdit ? (
            <button
              type="button"
              onClick={onEdit}
              className="flex flex-col items-center gap-1 rounded-xl bg-slate-50 text-slate-700 py-2.5 border border-slate-200"
            >
              <Pencil className="w-5 h-5" />
              <span className="text-[10px] font-semibold">Edit</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={onLedger}
              className="flex flex-col items-center gap-1 rounded-xl bg-slate-50 text-slate-700 py-2.5 border border-slate-200"
            >
              <BookOpen className="w-5 h-5" />
              <span className="text-[10px] font-semibold">Ledger</span>
            </button>
          )}
          <button
            type="button"
            onClick={onToggleMore}
            className="flex flex-col items-center gap-1 rounded-xl bg-slate-50 text-slate-700 py-2.5 border border-slate-200 relative"
          >
            <MoreHorizontal className="w-5 h-5" />
            <span className="text-[10px] font-semibold">More</span>
            {moreOpen && (
              <div className="absolute right-0 top-full mt-1 w-40 rounded-xl border border-slate-200 bg-white shadow-lg py-1 z-20 text-left">
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                  onClick={onLedger}
                >
                  {t('common.ledger')}
                </button>
                {canDelete && (
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                    onClick={onDelete}
                  >
                    {t('common.delete')}
                  </button>
                )}
              </div>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Helper kept for callers that still format standing with amount — unused amount is fine. */
export function formatStandingHint(amount: number) {
  return formatMoney(amount);
}
